import React from 'react';
import { StyleSheet, View } from 'react-native';
import { useStyle } from 'react-native-style-utilities';
import {
  theme,
  TitleText,
  useBreakpointHelpers,
  useFluidSize,
} from '@sweetgreen/garnish';

import {
  LoadingAnimation,
  PageWrapper,
  useAddressHandlers,
} from '@order/components';
import { useCustomer } from '@order/Customer';
import { useLocalizationContext } from '@order/Localization';

import { AddressForm } from '../../../../components/Address/AddressForm';
import type { Address } from '../../../../graphql/types';

export const CheckoutAddressForm = ({
  orderAddress,
}: CheckoutAddressFormProps) => {
  const { t } = useLocalizationContext();
  const { currentBreakpoint } = useBreakpointHelpers();
  const fluid = useFluidSize({ min: 375, max: 1440 });
  const marginVertical = fluid(24, 80);
  const contentMargin = useStyle(() => ({ marginVertical }), [marginVertical]);

  if (currentBreakpoint.isMobile) {
    return (
      <PageWrapper withHeaderTitle withoutFooter testID="checkout.address.form">
        <View style={styles.mobileContent}>
          <View style={styles.mobileTitle}>
            <TitleText style={styles.titleFont}>
              {t('delivery.complete-form.title')}
            </TitleText>
          </View>
          <CheckoutAddressFormContent orderAddress={orderAddress} />
        </View>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper withHeaderTitle withoutFooter testID="checkout.address.form">
      <View style={styles.container}>
        <View style={[styles.content, contentMargin]}>
          <TitleText size={4} style={styles.title}>
            {t('delivery.complete-form.title')}
          </TitleText>
          <View style={styles.form}>
            <CheckoutAddressFormContent orderAddress={orderAddress} />
          </View>
        </View>
      </View>
    </PageWrapper>
  );
};

const CheckoutAddressFormContent = ({
  orderAddress,
}: CheckoutAddressFormProps) => {
  const { t } = useLocalizationContext();
  const { isCustomerLoaded } = useCustomer();
  const { isUpdatingAddress, handleOnEdit } = useAddressHandlers({
    editingAddressId: orderAddress?.id,
  });

  if (!isCustomerLoaded) return <LoadingAnimation />;

  return (
    <AddressForm
      disableAddress
      noHeader
      submitCta={t('delivery.complete-form.cta')}
      address={orderAddress}
      deleting={false}
      visible={true}
      editing={true}
      loading={isUpdatingAddress}
      onEdit={handleOnEdit}
    />
  );
};

type CheckoutAddressFormProps = {
  readonly orderAddress?: Partial<Address>;
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'center',
    backgroundColor: theme.colors.KHAKI_2,
  },
  content: {
    width: 484,
  },
  mobileContent: {
    flex: 1,
    flexGrow: 1,
    backgroundColor: theme.colors.KHAKI_3,
  },
  form: {
    backgroundColor: theme.colors.KHAKI_3,
    borderRadius: theme.radius.medium,
  },
  title: {
    marginBottom: theme.spacing.xlarge,
  },
  titleFont: {
    fontSize: 32,
    fontFamily: 'Grenette Pro',
    fontWeight: '300',
    letterSpacing: -1.6,
    lineHeight: 36,
  },
  mobileTitle: {
    marginBottom: theme.spacing.large,
    marginHorizontal: theme.spacing.medium,
  },
});
